import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useMembers } from "@/context/MemberContext";
import { Calendar, Activity, Stethoscope, Pill, ClipboardList, Sparkles, HeartPulse, LogOut, Users, Crown, Shield, FileText, ChevronDown, Lock, TrendingUp } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";

const nav = [
  { to: "/dashboard", label: "Calendar", icon: Calendar, end: true, testid: "nav-calendar" },
  { to: "/vitals", label: "Vitals", icon: Activity, testid: "nav-vitals" },
  { to: "/trends", label: "Trends", icon: TrendingUp, testid: "nav-trends" },
  { to: "/symptoms", label: "Symptoms", icon: Sparkles, testid: "nav-symptoms" },
  { to: "/diagnostics", label: "Diagnostics", icon: ClipboardList, testid: "nav-diagnostics" },
  { to: "/consultations", label: "Consultations", icon: Stethoscope, testid: "nav-consultations" },
  { to: "/medications", label: "Medications", icon: Pill, testid: "nav-medications" },
  { to: "/profiles", label: "Profiles", icon: Users, testid: "nav-profiles" },
  { to: "/reports", label: "Share with doctor", icon: FileText, testid: "nav-reports" },
  { to: "/privacy", label: "Privacy & Data", icon: Lock, testid: "nav-privacy" },
  { to: "/subscription", label: "Subscription", icon: Crown, testid: "nav-subscription" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { members, active, setActive } = useMembers();
  const navigate = useNavigate();
  const initials = (user?.name || "U").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
  const isAdmin = !!user?.is_admin;

  return (
    <div className="app-shell">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-[#E5E7E4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <HeartPulse className="w-6 h-6 text-[#4A6750]" strokeWidth={1.5} />
            <span className="font-heading text-lg font-medium tracking-tight">Verda Health</span>
          </div>

          {/* Member selector — patient-only */}
          {!isAdmin && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="member-switcher" className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F9F8F6] border border-[#E5E7E4] hover:border-[#4A6750] transition-colors">
                <div className="w-6 h-6 rounded-full bg-[#E8ECE9] text-[#4A6750] flex items-center justify-center text-[10px] font-medium">
                  {(active?.name || "·").split(" ").map(s=>s[0]).slice(0,2).join("").toUpperCase()}
                </div>
                <span className="text-sm font-medium text-[#1C2E24] max-w-[120px] truncate">{active?.name || "Select"}</span>
                <ChevronDown className="w-3.5 h-3.5 text-[#7D8F84]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="w-64">
              <DropdownMenuLabel className="text-xs uppercase tracking-wider text-[#7D8F84]">Switch profile</DropdownMenuLabel>
              {members.map(m => (
                <DropdownMenuItem key={m.id} onClick={() => setActive(m.id)} data-testid={`member-option-${m.id}`}
                  className={`cursor-pointer ${active?.id === m.id ? "bg-[#E8ECE9]" : ""}`}>
                  <div className="flex flex-col">
                    <span className="font-medium">{m.name}</span>
                    <span className="text-xs text-[#7D8F84]">{m.relation || "Other"}{m.age ? ` · ${m.age} yrs` : ""}</span>
                  </div>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/profiles")} className="cursor-pointer text-[#4A6750]">
                <Users className="w-4 h-4 mr-2" /> Manage profiles
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2.5 px-3 py-1.5 rounded-full bg-[#F9F8F6] border border-[#E5E7E4]">
              <div className="w-7 h-7 rounded-full bg-[#4A6750] text-white flex items-center justify-center text-xs font-medium">{initials}</div>
              <span className="text-sm text-[#1C2E24] font-medium hidden sm:inline" data-testid="header-user-name">{user?.name}</span>
              {user?.tier === "pro" && <Crown className="w-3.5 h-3.5 text-[#C76B58]" />}
            </div>
            <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }}
              className="flex items-center gap-1.5 text-sm text-[#5C6D63] hover:text-[#C76B58] transition-colors px-2 py-1.5 rounded-lg">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 lg:gap-10">
        <nav className="lg:sticky lg:top-24 lg:self-start" data-testid="sidebar-nav">
          <ul className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
            {!isAdmin && nav.map(({ to, label, icon: Icon, end, testid }) => (
              <li key={to} className="flex-shrink-0">
                <NavLink end={end} to={to} data-testid={testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                      isActive ? "bg-[#E8ECE9] text-[#1C2E24]" : "text-[#5C6D63] hover:bg-[#F1F3F0] hover:text-[#1C2E24]"
                    }`
                  }>
                  <Icon className="w-4 h-4" strokeWidth={1.5} /> {label}
                </NavLink>
              </li>
            ))}
            {isAdmin && (
              <>
                <li className="flex-shrink-0">
                  <NavLink to="/admin" data-testid="nav-admin"
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                        isActive ? "bg-[#1C2E24] text-white" : "text-[#1C2E24] hover:bg-[#F1F3F0]"
                      }`
                    }>
                    <Shield className="w-4 h-4" strokeWidth={1.5} /> Admin
                  </NavLink>
                </li>
                <li className="flex-shrink-0">
                  <NavLink to="/privacy" data-testid="nav-privacy"
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                        isActive ? "bg-[#E8ECE9] text-[#1C2E24]" : "text-[#5C6D63] hover:bg-[#F1F3F0] hover:text-[#1C2E24]"
                      }`
                    }>
                    <Lock className="w-4 h-4" strokeWidth={1.5} /> Security &amp; Privacy
                  </NavLink>
                </li>
              </>
            )}
          </ul>
        </nav>
        <main className="min-w-0 fade-in">{children}</main>
      </div>
    </div>
  );
}
